<template>
  <PageLayout
    :is-loading="isLoading"
    page-title="Event"
    :show-banner="true"
    :banner-items="bannerItems"
    :show-sponsors="false"
    :banner-location="'event'"
    sponsor-title="Sponsor Hadiah Bert Points"
    @banner-click="handleBannerClick"
  >
    <!-- Event List Section -->
    <div class="event-section">
      <div class="section-header">
        <h3 class="section-title">Daftar Event</h3>
      </div>
      
      <!-- Event Grid or Empty State -->
      <div v-if="events.length > 0" class="event-grid">
        <div v-for="(event, index) in events" :key="index" class="event-card">
          <div class="event-image">
            <img :src="getEventImage(event)" :alt="event.name" class="event-photo" />
            <div class="event-overlay">
              <div class="event-date-badge">
                <div class="event-day">{{ formattedDate(event.start_date) }}</div>
                <div class="event-month">{{ formattedMonth(event.start_date) }}</div>
              </div>
            </div>
          </div>
          
          <div class="event-content">
            <div class="event-header">
              <h4 class="event-title">{{ event.name }}</h4>
              <div class="event-actions-top">
                <button @click="addToCalendar(event)" class="action-button notification-btn" title="Tambah ke Kalender">
                  <i class="mdi mdi-bell"></i>
                </button>
                <button @click="shareEvent(event)" class="action-button share-btn" title="Bagikan Event">
                  <i class="mdi mdi-share-variant"></i>
                </button>
              </div>
            </div>
            
            <div class="event-status">
              <span 
                class="badge" 
                :class="{
                  'badge-success': event.status === 'ongoing',
                  'badge-info': event.status === 'upcoming',
                  'badge-secondary': event.status === 'past'
                }"
              >
                {{ getStatusText(event.status) }}
              </span>
            </div>
            
            <div class="event-info">
              <div class="event-meta">
                <div class="event-date-range">
                  <i class="mdi mdi-calendar"></i>
                  {{ event.date_range }}
                </div>
                <div class="event-duration">
                  <i class="mdi mdi-clock-outline"></i>
                  {{ event.duration_in_days }} hari
                </div>
              </div>
            </div>
            
            <div class="event-footer">
              <button class="detail-button" @click="viewEventDetail(event)">
                Lihat Detail
                <i class="mdi mdi-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-state-content">
          <div class="empty-state-icon">
            <i class="mdi mdi-calendar-blank"></i>
          </div>
          <h3 class="empty-state-title">Belum Ada Event</h3>
          <p class="empty-state-description">
            Saat ini belum ada event yang tersedia. Silakan cek kembali nanti untuk melihat event-event menarik yang akan datang.
          </p>
          <button class="refresh-button" @click="loadEvents">
            <i class="mdi mdi-refresh"></i>
            Muat Ulang
          </button>
        </div>
      </div>
    </div>

    <!-- Event Detail Modal -->
    <div v-if="showEventDetail" class="event-detail-overlay" @click="closeEventDetail">
      <div class="event-detail-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ selectedEvent.name }}</h3>
          <button class="close-button" @click="closeEventDetail">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        
        <div class="modal-content">
          <div class="event-info-grid">
            <div class="info-item">
              <span class="info-label">Status:</span>
              <span class="info-value">{{ getStatusText(selectedEvent.status) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Tanggal Mulai:</span>
              <span class="info-value">{{ selectedEvent.formatted_start_date }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Tanggal Berakhir:</span>
              <span class="info-value">{{ selectedEvent.formatted_end_date }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Durasi:</span>
              <span class="info-value">{{ selectedEvent.duration_in_days }} hari</span>
            </div>
            <div v-if="selectedEvent.link_event" class="info-item">
              <span class="info-label">Link Event:</span>
              <span class="info-value">
                <a :href="selectedEvent.link_event" target="_blank" class="text-decoration-none">
                  Buka Link Event
                  <i class="mdi mdi-open-in-new"></i>
                </a>
              </span>
            </div>
          </div>
          
          <div class="event-description">
            <h4>Deskripsi Event</h4>
            <div class="description-content" v-html="formatDescription(selectedEvent.description)"></div>
          </div>

          <!-- Calendar Integration -->
          <div class="calendar-integration">
            <h4>Tambah ke Kalender</h4>
            <div class="calendar-buttons">
              <button @click="addToGoogleCalendar(selectedEvent)" class="btn btn-outline-primary">
                <i class="mdi mdi-google"></i>
                Google Calendar
              </button>
              <button @click="addToOutlookCalendar(selectedEvent)" class="btn btn-outline-info">
                <i class="mdi mdi-microsoft-outlook"></i>
                Outlook
              </button>
              <button @click="downloadICS(selectedEvent)" class="btn btn-outline-secondary">
                <i class="mdi mdi-download"></i>
                Download ICS
              </button>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeEventDetail">Tutup</button>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <div 
        v-for="toast in toasts" 
        :key="toast.id"
        class="toast"
        :class="{
          'toast-success': toast.type === 'success',
          'toast-warning': toast.type === 'warning',
          'toast-error': toast.type === 'error'
        }"
        @click="removeToast(toast.id)"
      >
        <div class="toast-content">
          <i 
            class="toast-icon"
            :class="{
              'mdi mdi-check-circle': toast.type === 'success',
              'mdi mdi-alert-circle': toast.type === 'warning',
              'mdi mdi-close-circle': toast.type === 'error'
            }"
          ></i>
          <span class="toast-message">{{ toast.message }}</span>
        </div>
        <button class="toast-close" @click.stop="removeToast(toast.id)">
          <i class="mdi mdi-close"></i>
        </button>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import PageLayout from '@/components/ui/PageLayout.vue';

export default {
  name: "NewEventUser",
  components: { 
    PageLayout
  },
  data() {
    return {
      isLoading: false,
      bannerItems: [],
      events: [],
      showEventDetail: false,
      selectedEvent: {},
      toasts: []
    };
  },
  methods: {
    loadBanners() {
      const baseURI = this.$settings.endPoint + "get-banner";
      this.isLoading = true;
      return this.$http
        .get(
          baseURI +
          `?limit=10&page=1&sort_field=&sort_type=&title=&link=&priority=&location=event`,
          {
            timeout: 10000,
            'headers': {
              'AppToken': this.$settings.jwtSecret,
            }
          }
        )
        .then((response) => {
          this.isLoading = false;
          this.bannerItems = response.data.datas;
        }).catch(error => {
          this.isLoading = false;
          console.log('Error:', error);
          this.handleError(error);
        });
    },
    
    loadEvents() {
      const baseURI = this.$settings.endPoint + "information-events/public";
      this.isLoading = true;
      return this.$http
        .get(
          baseURI +
          `?limit=10&status=active`,
          { 
            timeout: 10000,
            'headers': {
              'AppToken': this.$settings.jwtSecret,
            }
          }
        )
        .then((response) => {
          this.isLoading = false;
          this.events = response.data.datas || [];
        }).catch(error => {
          this.isLoading = false;
          console.log('Error:', error);
          this.handleError(error);
        });
    },
    
    handleBannerClick(banner) {
      console.log('Banner clicked:', banner);
      if (banner.link && banner.link !== '#') {
        window.open(banner.link, '_blank');
      }
    },
    
    viewEventDetail(event) {
      this.selectedEvent = event;
      this.showEventDetail = true;
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    },
    
    closeEventDetail() {
      this.showEventDetail = false;
      this.selectedEvent = {};
      document.body.style.overflow = 'auto'; // Restore scroll
    },

    getEventImage(event) {
      if (event.image && event.image.trim() !== '') {
        return event.image;
      }
      return require('@/assets/cta-background.png');
    },

    getStatusText(status) {
      switch (status) {
        case 'upcoming':
          return 'Akan Datang';
        case 'ongoing':
          return 'Sedang Berlangsung';
        case 'past':
          return 'Selesai';
        default:
          return status;
      }
    },

    shareEvent(event) {
      if (event.link_event) {
        // Copy the event link to clipboard
        this.copyToClipboard(event.link_event);
      } else {
        // If no link_event, show a message
        this.showToast('Event link tidak tersedia', 'warning');
      }
    },

    copyToClipboard(text) {
      // Try using the modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.showToast('Link berhasil disalin!', 'success');
        }).catch(() => {
          this.fallbackCopyToClipboard(text);
        });
      } else {
        // Fallback for older browsers or non-secure contexts
        this.fallbackCopyToClipboard(text);
      }
    },

    fallbackCopyToClipboard(text) {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        this.showToast('Link berhasil disalin!', 'success');
      } catch (err) {
        this.showToast('Gagal menyalin link', 'error');
      } finally {
        document.body.removeChild(textArea);
      }
    },

    showToast(message, type = 'success') {
      // Create toast notification
      const toast = {
        id: Date.now(),
        message: message,
        type: type,
        show: true
      };
      
      this.toasts.push(toast);
      
      // Auto remove toast after 3 seconds
      setTimeout(() => {
        this.removeToast(toast.id);
      }, 3000);
    },

    removeToast(toastId) {
      const index = this.toasts.findIndex(toast => toast.id === toastId);
      if (index > -1) {
        this.toasts.splice(index, 1);
      }
    },

    addToCalendar(event) {
      // Show toast notification
      this.showToast('Event berhasil ditambahkan ke kalender!', 'success');
      this.addToGoogleCalendar(event);
    },

    // Calendar integration methods
    addToGoogleCalendar(event) {
      if (!event) return;
      
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      
      const googleUrl = new URL('https://calendar.google.com/calendar/render');
      googleUrl.searchParams.set('action', 'TEMPLATE');
      googleUrl.searchParams.set('text', event.name);
      googleUrl.searchParams.set('dates', 
        `${this.formatDateForCalendar(startDate)}/${this.formatDateForCalendar(endDate)}`
      );
      googleUrl.searchParams.set('details', event.description || '');
      if (event.link_event) {
        googleUrl.searchParams.set('location', event.link_event);
      }
      
      window.open(googleUrl.toString(), '_blank');
    },

    addToOutlookCalendar(event) {
      if (!event) return;
      
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      
      const outlookUrl = new URL('https://outlook.live.com/calendar/0/deeplink/compose');
      outlookUrl.searchParams.set('subject', event.name);
      outlookUrl.searchParams.set('startdt', startDate.toISOString());
      outlookUrl.searchParams.set('enddt', endDate.toISOString());
      outlookUrl.searchParams.set('body', event.description || '');
      if (event.link_event) {
        outlookUrl.searchParams.set('location', event.link_event);
      }
      
      window.open(outlookUrl.toString(), '_blank');
    },

    downloadICS(event) {
      if (!event) return;
      
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//BERT//Information Events//EN',
        'BEGIN:VEVENT',
        `UID:${event.id}@bert-events.com`,
        `DTSTART:${this.formatDateForICS(startDate)}`,
        `DTEND:${this.formatDateForICS(endDate)}`,
        `SUMMARY:${event.name}`,
        `DESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}`,
        event.link_event ? `URL:${event.link_event}` : '',
        `DTSTAMP:${this.formatDateForICS(new Date())}`,
        'END:VEVENT',
        'END:VCALENDAR'
      ].filter(line => line !== '').join('\r\n');
      
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
      link.click();
    },

    formatDateForCalendar(date) {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    },

    formatDateForICS(date) {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    },

    formatDescription(description) {
      if (!description) return '';
      return description.replace(/\n/g, '<br>');
    },
    
    formattedDate(inputDate) {
      if (!inputDate) return '';
      const dateObj = new Date(inputDate);
      const day = dateObj.getDate();
      return day < 10 ? `0${day}` : day.toString();
    },
    
    formattedMonth(inputDate) {
      if (!inputDate) return '';
      const dateObj = new Date(inputDate);
      const monthAbbreviation = new Intl.DateTimeFormat('id', { month: 'short' }).format(dateObj);
      return monthAbbreviation;
    },
    
    handleError(error) {
      if (error.response) {
        if (error.response.status === 422) {
          console.error('Validation error:', error.response.data.errors);
        } else if (error.response.status === 500) {
          this.$router.push('/server-error');
        } else if (error.response.status === 401) {
          this.$router.push("/authorized-error");
        } else {
          this.$router.push('/page-not-found');
        }
      }
    },
  },
  mounted() {
    this.loadBanners();
    this.loadEvents();
  }
};
</script>

<style scoped>
/* ========================================= */
/* EVENT SECTION STYLES                      */
/* ========================================= */
.event-section {
  padding: 24px 32px;
  margin: 0 auto 32px auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  max-width: 1200px;
  width: calc(100% - 64px);
}

.section-header {
  margin-bottom: 24px;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.event-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 24px;
}

.event-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
}

.event-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.event-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.event-photo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.event-card:hover .event-photo {
  transform: scale(1.05);
}

.event-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  padding: 16px;
}

.event-date-badge {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 12px 16px;
  text-align: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.event-day {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1F2937;
  line-height: 1;
}

.event-month {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  margin-top: 2px;
}

.event-content {
  padding: 20px;
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.event-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0;
  line-height: 1.3;
  flex: 1;
  margin-right: 12px;
}

.event-actions-top {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;
}

.notification-btn {
  background: #F59E0B;
  color: white;
}

.notification-btn:hover {
  background: #D97706;
  transform: scale(1.05);
}

.share-btn {
  background: #F59E0B;
  color: white;
}

.share-btn:hover {
  background: #D97706;
  transform: scale(1.05);
}

.event-status {
  margin-bottom: 16px;
}

.event-status .badge {
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.badge-success {
  background-color: #10B981;
  color: white;
}

.badge-info {
  background-color: #3B82F6;
  color: white;
}

.badge-secondary {
  background-color: #6B7280;
  color: white;
}

.event-duration {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #6B7280;
}

.event-duration i {
  font-size: 1rem;
  color: #9CA3AF;
}

.event-info {
  margin-bottom: 20px;
}

.event-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.event-date-range,
.event-location {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #6B7280;
}

.event-date-range i,
.event-location i {
  font-size: 1rem;
  color: #9CA3AF;
}

.event-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #F3F4F6;
}

.detail-button {
  background: #F59E0B;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
}

.detail-button:hover {
  background: #D97706;
  transform: translateY(-1px);
}

/* ========================================= */
/* EVENT DETAIL MODAL STYLES                 */
/* ========================================= */
.event-detail-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.event-detail-modal {
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #EAECF0;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6B7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.3s ease;
}

.close-button:hover {
  background: #F3F4F6;
}

.modal-content {
  padding: 24px;
}

.event-info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #F3F4F6;
}

.info-label {
  font-weight: 600;
  color: #374151;
}

.info-value {
  color: #6B7280;
  text-align: right;
}

.points-highlight {
  color: #4A90E2 !important;
  font-weight: 700 !important;
}

.event-description h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px 0;
}

.description-content {
  color: #374151;
  line-height: 1.6;
  font-size: 0.95rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #EAECF0;
}

.btn-secondary {
  background: #F3F4F6;
  color: #374151;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
}

.btn-secondary:hover {
  background: #E5E7EB;
}

.btn-primary {
  background: #4A90E2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
}

.btn-primary:hover {
  background: #357ABD;
}

/* ========================================= */
/* TOAST NOTIFICATION STYLES                 */
/* ========================================= */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.toast {
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 300px;
  max-width: 400px;
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: slideInRight 0.3s ease;
  border-left: 4px solid #E5E7EB;
}

.toast:hover {
  transform: translateX(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
}

.toast-success {
  border-left-color: #10B981;
  background: linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%);
}

.toast-warning {
  border-left-color: #F59E0B;
  background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%);
}

.toast-error {
  border-left-color: #EF4444;
  background: linear-gradient(135deg, #FEF2F2 0%, #FECACA 100%);
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.toast-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.toast-success .toast-icon {
  color: #10B981;
}

.toast-warning .toast-icon {
  color: #F59E0B;
}

.toast-error .toast-icon {
  color: #EF4444;
}

.toast-message {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.4;
}

.toast-close {
  background: none;
  border: none;
  color: #9CA3AF;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 1rem;
  flex-shrink: 0;
  margin-left: 8px;
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #6B7280;
}

/* Toast animations */
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* ========================================= */
/* CALENDAR INTEGRATION STYLES               */
/* ========================================= */
.calendar-integration {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #F3F4F6;
}

.calendar-integration h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 16px 0;
}

.calendar-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.calendar-buttons .btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  border: 2px solid;
  background: white;
  flex: 1;
  justify-content: center;
  min-width: 140px;
}

.btn-outline-primary {
  color: #4285F4;
  border-color: #4285F4;
}

.btn-outline-primary:hover {
  background: #4285F4;
  color: white;
  transform: translateY(-1px);
}

.btn-outline-info {
  color: #0078D4;
  border-color: #0078D4;
}

.btn-outline-info:hover {
  background: #0078D4;
  color: white;
  transform: translateY(-1px);
}

.btn-outline-secondary {
  color: #6B7280;
  border-color: #6B7280;
}

.btn-outline-secondary:hover {
  background: #6B7280;
  color: white;
  transform: translateY(-1px);
}

/* ========================================= */
/* TOAST ANIMATIONS                          */
/* ========================================= */
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* ========================================= */
/* MOBILE RESPONSIVE STYLES                  */
/* ========================================= */
@media (max-width: 768px) {
  .event-section {
    width: calc(100% - 40px);
    margin: 0 20px 24px 20px;
    padding: 16px;
  }
  
  .section-title {
    font-size: 1.1rem;
  }

  .event-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .event-card {
    border-radius: 12px;
  }
  
  .event-image {
    height: 160px;
  }
  
  .event-overlay {
    padding: 12px;
  }
  
  .event-date-badge {
    padding: 8px 12px;
  }
  
  .event-day {
    font-size: 1.2rem;
  }
  
  .event-month {
    font-size: 0.7rem;
  }
  
  .event-content {
    padding: 16px;
  }
  
  .event-header {
    align-items: flex-start;
    gap: 8px;
  }
  
  .event-title { 
    font-size: 1rem;
    margin-right: 8px;
  }
  
  .event-actions-top {
    gap: 6px;
  }
  
  .action-button {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }
  
  .event-community {
    font-size: 0.75rem;
  }
  
  .event-meta {
    gap: 6px;
  }
  
  .event-date-range,
  .event-location {
    font-size: 0.8rem;
  }
  
  .event-footer {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .detail-button {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
  }

  .calendar-buttons {
    flex-direction: column;
  }
  
  .calendar-buttons .btn {
    width: 100%;
    justify-content: center;
  }

  /* Empty State Mobile */
  .empty-state {
    min-height: 300px;
    padding: 20px 10px;
  }
  
  .empty-state-content {
    max-width: 300px;
  }
  
  .empty-state-icon i {
    font-size: 3rem;
  }
  
  .empty-state-title {
    font-size: 1.25rem;
  }
  
  .empty-state-description {
    font-size: 0.9rem;
  }
  
  .refresh-button {
    width: 100%;
    justify-content: center;
    padding: 14px 20px;
  }

  /* Modal Mobile */
  .event-detail-overlay {
    padding: 10px;
  }
  
  .event-detail-modal {
    max-height: 95vh;
  }
  
  .modal-header,
  .modal-content,
  .modal-footer {
    padding: 16px;
  }
  
  .modal-title {
    font-size: 1.1rem;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .info-value {
    text-align: left;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .btn-secondary,
  .btn-primary {
    width: 100%;
  }

  /* Toast Mobile */
  .toast-container {
    top: 10px;
    right: 10px;
    left: 10px;
    align-items: stretch;
  }
  
  .toast {
    min-width: auto;
    max-width: none;
    width: 100%;
  }
  
  .toast-message {
    font-size: 0.85rem;
  }
}

/* ========================================= */
/* EMPTY STATE STYLES                        */
/* ========================================= */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 40px 20px;
}

.empty-state-content {
  text-align: center;
  max-width: 400px;
}

.empty-state-icon {
  margin-bottom: 24px;
}

.empty-state-icon i {
  font-size: 4rem;
  color: #D1D5DB;
}

.empty-state-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 12px 0;
}

.empty-state-description {
  font-size: 1rem;
  color: #6B7280;
  line-height: 1.6;
  margin: 0 0 24px 0;
}

.refresh-button {
  background: #4A90E2;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.refresh-button:hover {
  background: #357ABD;
  transform: translateY(-1px);
}

.refresh-button i {
  font-size: 1.1rem;
}
</style>